import type { ServiceNameType } from '@looming-tech/avani-infra-v2-shared';
import type { DateTime } from 'luxon';
import type { ClientModel } from './clients.types';
import type { CommentModel, CommentParams } from './comments.types';
import type { BaseModel } from './common.types';
import type { MediaData } from './medias.types';
import type { SubModelType } from './services.types';
import type { UserModel } from './users.types';
import type { VenueModel } from './venues.types';
import type { VisitActiveBarModel, VisitBarModel, VisitBarParams } from './visits-bars.types';
import type { VisitCellarModel, VisitCellarParams } from './visits-cellars.types';
import type { VisitFaultInteractionsModel } from './visits-fault-interactions.types';
import type { VisitFobParams } from './visits-fob.types';
import type { VisitGlassWasherParams } from './visits-glass-washer.types';
import type { VisitPythonModel } from './visits-pythons.types';
import type { VisitRemoteCoolerParams } from './visits-remote-coolers.types';
import type { VisitTapParams } from './visits-tap.types';

export enum VisitStatus {
  PROCESSING = 'Processing',
  FAILED = 'Failed',
  INITIATED = 'Initiated',
  PARTIAL = 'Partial',
  AWAITING_CHECKOUT = 'Awaiting Checkout',
  COMPLETED = 'Completed',
  ARCHIVED = 'Archived'
}

export interface VisitModel extends BaseModel {
  mainUserId: string;
  status: VisitStatus;
  startedAt: string;
  checkedOutAt?: string;
  serviceName: ServiceNameType;
  venueName: string;
  reportedTo?: string;
  subCycleWeeks?: number;
  subPrice?: string;
  subStart?: string;
  subEnd?: string;
  serviceModelSub?: SubModelType;
  isStitched?: boolean;
  // Relations
  subId: string;
  venueId: string;
  clientId: string;
  // Collections
  mainUser: UserModel;
  users: UserModel[];
  visitBars: VisitBarModel[];
  visitCellars: VisitCellarModel[];
  visitPythons: VisitPythonModel[];
  visitFaultInteractions: VisitFaultInteractionsModel[];
  comments: CommentModel[];
  activeBars: VisitActiveBarModel[];
  medias: MediaData[];
}

export type VisitsListModel = Pick<
  VisitModel,
  | 'id'
  | 'venueId'
  | 'venueName'
  | 'serviceName'
  | 'mainUser'
  | 'users'
  | 'startedAt'
  | 'checkedOutAt'
  | 'status'
  | 'activeBars'
  | 'isStitched'
> & {
  clientId: string;
  clientName: string;
  barsCount: number;
  tapsCount: number;
  washersCount: number;
  totalFaultsCount: number;
};

export interface ListVisitsResponse {
  result: VisitsListModel[];
  limit: number;
  offset: number;
  count: number;
}

export interface CreateVisitParams extends Pick<VisitModel, 'startedAt' | 'status' | 'checkedOutAt'> {
  subscriptionId: string;
  venueId: string;
  mainUserId: string;
  users: string[];
}

export interface VisitIdParam {
  visitId: string;
}

export interface UpdateVisitParams extends CreateVisitParams, VisitIdParam {}

export interface UpdateVisitFaultInteractionsParams extends VisitIdParam {
  faultInteractions?: VisitFaultInteractionsModel[];
}

export interface DeleteVisitFaultsParams extends VisitIdParam {
  visitFaultId: string;
}

export interface UpdateVisitCommentsParams extends VisitIdParam {
  comments?: CommentParams[];
}

export interface UpdateVisitCellarsParams extends VisitIdParam {
  cellars?: VisitCellarParams[];
  fobs?: VisitFobParams[];
  remotes?: VisitRemoteCoolerParams[];
}

export interface UpdateVisitBarsParams {
  visitId: string;
  bars?: VisitBarParams[];
  taps?: VisitTapParams[];
  glassWashers?: VisitGlassWasherParams[];
}

export interface DeleteVisitParams extends VisitIdParam {}

export type VisitSortingFields =
  | 'venueName'
  | 'serviceName'
  | 'mainTechnician'
  | 'clientName'
  | 'status'
  | 'startedAt'
  | 'barsCount'
  | 'tapsCount'
  | 'washersCount'
  | 'totalFaultsCount';

export interface VisitsFiltersProps {
  venue?: VenueModel;
  serviceName?: ServiceNameType;
  from?: DateTime;
  to?: DateTime;
  status?: VisitStatus;
  mainTechnician?: UserModel;
  client?: ClientModel;
  isStitched?: boolean;
}
